import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MessageSquare, MessageCircle, FileEdit, Zap, History, CheckCircle2, Clock, XCircle, Info } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { CHANNEL_LABEL, TRIGGER_LABEL, formatDateTime, friendlyStatus, statusIcon } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };

export function CommsOverview() {
    const [templates, setTemplates] = useState(null);
    const [rules, setRules] = useState(null);
    const [queue, setQueue] = useState(null);
    const [stats, setStats] = useState(null);
    const [error, setError] = useState(null);

    const load = () => {
        setError(null);
        Promise.all([
            staffApi.commsListTemplates(),
            staffApi.commsListRules(),
            staffApi.commsListQueue({ limit: 25 }),
        ]).then(([t, r, q]) => {
            setTemplates(t.templates || []);
            setRules(r.rules || []);
            setQueue(q.items || []);
            setStats(q.stats || {});
        }).catch(setError);
    };
    useEffect(load, []);

    const templateCounts = useMemo(() => {
        if (!templates) return null;
        return {
            total: templates.length,
            published: templates.filter((t) => t.status === "published").length,
            draft: templates.filter((t) => t.status === "draft").length,
        };
    }, [templates]);

    const activeAutomations = useMemo(() => (rules || []).filter((r) => r.active).length, [rules]);
    const upcoming = useMemo(
        () => (queue || []).filter((q) => ["scheduled", "pending"].includes(q.status)).slice(0, 6),
        [queue]
    );
    const sentCount = stats?.sent ?? 0;
    const scheduledCount = (stats?.scheduled ?? 0) + (stats?.pending ?? 0);
    const failedCount = stats?.failed ?? 0;

    if (error) return <ErrorState error={error} onRetry={load} />;

    return (
        <div className="space-y-6" data-testid="comms-overview">
            {/* Friendly KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Templates" value={templateCounts ? templateCounts.total : "…"}
                     sub={templateCounts ? `${templateCounts.published} live · ${templateCounts.draft} draft` : ""}
                     testId="kpi-templates" icon={FileEdit} to="templates" />
                <Kpi label="Active automations" value={rules ? activeAutomations : "…"}
                     sub={rules ? `${(rules.length - activeAutomations)} paused` : ""}
                     testId="kpi-automations" icon={Zap} to="automations" />
                <Kpi label="Scheduled" value={stats ? scheduledCount : "…"}
                     sub="waiting to go out" testId="kpi-scheduled" icon={Clock} to="history" />
                <Kpi label="Sent" value={sentCount} sub={failedCount > 0 ? `${failedCount} needs attention` : "recent activity"}
                     testId="kpi-sent" icon={CheckCircle2} to="history" />
            </div>

            {/* Upcoming */}
            <div className="rounded-2xl glass p-5" data-testid="comms-upcoming">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Coming up</div>
                        <h2 className="font-serif text-xl text-white mt-1">Next scheduled messages</h2>
                    </div>
                    <Link to="history" className="text-xs text-gold hover:underline">All messages →</Link>
                </div>
                {!queue ? <Skeleton className="h-24" /> : upcoming.length === 0 ? (
                    <p className="text-sm text-white/50">Nothing scheduled right now. Switch on an automation to start queuing messages.</p>
                ) : (
                    <ul className="divide-y divide-white/5">
                        {upcoming.map((q) => {
                            const Icon = CHANNEL_ICON[q.channel] || Mail;
                            return (
                                <li key={q.id} className="py-2 flex items-center gap-3" data-testid={`upcoming-row-${q.id}`}>
                                    <div className="h-8 w-8 rounded-full glass flex items-center justify-center shrink-0">
                                        <Icon className="h-3.5 w-3.5 text-gold" strokeWidth={1.5} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm text-white truncate">{q.template_name || "—"}</div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 flex flex-wrap gap-2 mt-0.5">
                                            <span>{TRIGGER_LABEL[q.trigger] || q.trigger}</span>
                                            <span>·</span>
                                            <span>{CHANNEL_LABEL[q.channel]}</span>
                                            <span>·</span>
                                            <span>{formatDateTime(q.scheduled_at)}</span>
                                        </div>
                                    </div>
                                    <span className="text-[11px] text-white/70 inline-flex items-center gap-1">
                                        {statusIcon(q.status)} {friendlyStatus(q.status)}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* Shortcuts */}
            <div className="grid gap-3 md:grid-cols-3">
                <QuickLink to="templates" title="Write a message" desc="Draft what you'd say to a client — email, SMS or WhatsApp — using placeholders for names, dates and services." icon={FileEdit} testId="quicklink-templates" />
                <QuickLink to="automations" title="Turn on an automation" desc="Pick from booking reminders, aftercare check-ins, review requests, rebooking nudges and birthdays." icon={Zap} testId="quicklink-automations" />
                <QuickLink to="history" title="See what went out" desc="Every message — scheduled, sent, failed — in one time-sorted view." icon={History} testId="quicklink-history" />
            </div>
        </div>
    );
}

function Kpi({ label, value, sub, testId, icon: Icon, to }) {
    // All four cards share the same navy `glass` surface used by the
    // "Coming up" panel so the Communications Overview reads as a
    // consistent premium set. No per-card tone variant.
    const inner = (
        <>
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/55">{label}</div>
                {Icon && <Icon className="h-4 w-4 text-gold/80" strokeWidth={1.5} />}
            </div>
            <div className="mt-2 font-serif text-3xl text-white">{value}</div>
            {sub && <div className="text-[11px] text-white/60 mt-1">{sub}</div>}
        </>
    );
    const cls = "rounded-2xl glass hairline p-4 block hover:border-gold/50 transition-colors";
    return to ? (
        <Link to={to} className={cls} data-testid={testId}>{inner}</Link>
    ) : (
        <div className={cls} data-testid={testId}>{inner}</div>
    );
}

function QuickLink({ to, title, desc, icon: Icon, testId }) {
    return (
        <Link to={to} data-testid={testId} className="rounded-2xl glass border hairline p-4 hover:border-gold/50 transition-colors block">
            <div className="flex items-center gap-2 text-gold mb-2">
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                <div className="text-[10px] uppercase tracking-[0.2em]">Shortcut</div>
            </div>
            <div className="text-white font-ui text-sm">{title}</div>
            <div className="text-[11px] text-white/55 mt-1 leading-relaxed">{desc}</div>
        </Link>
    );
}
